//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useRef, useState } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useFormik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { AFITD_DOMAIN, PATH_HOME } from '../../system/constants';
import { logPageView } from '../../system/firebase';
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { PAGE_TITLE_EMPLOYERS, RECAPTCHA_SITE_KEY, REQUIRED, YOU_MUST_ACCEPT_TERMS_BEFORE_SUBMITTING } from '../../common/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { validateEmail } from '../../common/emailHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import { submitSummerIntershipEmployerRegistrationForm } from '../../common/services/customerSupportServices';
import { getCopyrightTextInternship } from '../../common/textHelper';
import {
    PLACEHOLDER_FIELD_COMPANY_NAME,
    PLACEHOLDER_FIELD_EMAIL,
    PLACEHOLDER_FIELD_FIRST_NAME,
    PLACEHOLDER_FIELD_JOB_TITLE,
    PLACEHOLDER_FIELD_LAST_NAME,
    PLACEHOLDER_FIELD_PHONE,
} from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import internshipBanner from '../../images/internship/internship-banner.png';
import internshipImg from '../../images/internship/internship-img.jpg';

import circle from '../../images/Banner/circle.png';
import Footer from '../../home/subComponents/Footer';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdPageHead from '../../common/elemental/AfitdPageHead';
import AfitdTypography from '../../common/elemental/AfitdTypography';

// import CreateAccountModal from './CreateAccountModal';
import PhoneInput, { formatPhoneNumberIntl, isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input/input';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdCheckbox from '../../common/elemental/AfitdCheckbox';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import LoginDialog from '../../topNav/subComponents/LoginDialog';
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _forEach from 'lodash/forEach';
import _pick from 'lodash/pick';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        // overflowX: 'hidden',
    },
    bannerContainer: {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),url('${internshipBanner}')`,
        height: '710px',
        position: 'relative',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        [theme.breakpoints.down('lg')]: { justifyContent: 'center' },
        [theme.breakpoints.down('md')]: { height: 490 },
        [theme.breakpoints.down('sm')]: { height: 496, textAlign: 'center' },
    },
    bannerContent1: {
        position: 'relative',
        paddingTop: 250,
        maxWidth: 1000,
        [theme.breakpoints.down('md')]: { paddingTop: 120 },
        [theme.breakpoints.down('sm')]: { paddingTop: 80 },
    },
    bannerContent2: { position: 'absolute', right: 0, top: 0 },
    bannerImageBottomContainer: { position: 'absolute', bottom: -4, right: 0 },
    bannerHeading: { fontSize: 36, fontWeight: 500, margin: 0, color: 'white', textTransform: 'uppercase', textAlign: 'center', [theme.breakpoints.down('md')]: { fontSize: 18 } },
    bannerParagraph: {
        margin: 0,
        marginTop: 24,
        fontWeight: 700,
        fontSize: '54px',
        textAlign: 'center',
        color: 'white',
        [theme.breakpoints.down('md')]: { fontSize: 40, marginTop: 16 },
        [theme.breakpoints.down('sm')]: { fontSize: 28 },
    },
    bannerButton: { padding: '12px 44px', fontSize: 20, [theme.breakpoints.down('sm')]: { fontSize: 15, padding: '10px 18px', width: '100%' } },
    circleImage: { paddingTop: 200 },
    buttonContainer: { display: 'flex', justifyContent: 'center', paddingTop: 32, [theme.breakpoints.down('md')]: { paddingTop: 24 } },
    internshipProgramContainer: {
        margin: '80px 0px',
        maxWidth: 790,
        [theme.breakpoints.down('md')]: { padding: '0px 30px', maxWidth: 705, margin: '60px 0px' },
        [theme.breakpoints.down('sm')]: { padding: '0px 18px', textAlign: 'center', margin: '40px 0px' },
    },
    internshipParagraph: {
        margin: 0,
        fontWeight: 600,
        fontSize: 22,
        color: '#111E14',
        [theme.breakpoints.down('md')]: { fontSize: 20 },
        [theme.breakpoints.down('sm')]: { fontSize: 18 },
    },
    internshipImage: {
        width: '100%',
    },
    internshipImgContainer: {
        marginBottom: 80,
        [theme.breakpoints.down('md')]: { marginBottom: 60 },
        [theme.breakpoints.down('sm')]: { marginBottom: 40 },
    },
    internshipProgramTextContainer: {
        maxWidth: 775,
        marginBottom: 120,
        [theme.breakpoints.down('md')]: { marginBottom: 60, padding: '0px 30px' },
        [theme.breakpoints.down('sm')]: { marginBottom: 40, padding: '0px 18px' },
    },
    internshipProgram: {
        fontWeight: 400,
        fontSize: 20,
        margin: 0,
    },
    internshipLink: { color: '#1d8a8b', fontWeight: 'bold', textDecoration: 'underline', marginRight: 6 },

    formInputContainer: {
        marginBottom: '20px',
    },
    formInputItem: {
        label: {
            color: 'red',
        },
    },
    inputPaddingRight: { paddingRight: '10px', [theme.breakpoints.down('sm')]: { paddingRight: '0px' } },
    fieldLevel: { fontWeight: 'bold' },
    firstName: { marginTop: '10px' },
    lastName: { marginTop: '10px' },
    contactsFormSection: {
        marginBottom: '60px',
        [theme.breakpoints.down('sm')]: { marginBottom: 52 },
    },
    contactsFormGrid: {
        border: '2px solid #1d8a8b',
        boxShadow: '0px 14.96px 20px rgba(0, 0, 0, 0.07)',
        borderRadius: '8px',
        padding: '48px 112px 60px 112px',
        width: '100%',
        [theme.breakpoints.down('md')]: { padding: '48px 39px 60px 39px' },
    },
    registrationFormTitle: {
        fontWeight: 'bold',
        fontSize: '26px',
        lineHeight: '30px',
        textAlign: 'center',
        marginBottom: 48,
        [theme.breakpoints.down('sm')]: { marginBottom: 24 },
    },
    inputPaddingLeft: { paddingLeft: '10px', [theme.breakpoints.down('sm')]: { paddingLeft: '0px', marginTop: 20 } },
    email: { marginTop: '10px' },
    phone: { marginTop: '10px' },
    company: { marginTop: '10px' },
    jobTitle: { marginTop: '10px' },
    phoneInput: {
        backgroundColor: '#fafafa',
        fontSize: '16px',
        '&:focus': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #c4c4c4',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputError: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `#f44336 !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #f44336',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },

    phoneErrorText: {
        color: '#f44336',
        fontSize: '12px',
        marginLeft: '14px',
        marginTop: '3px',
    },
    city: { marginTop: 10 },
    state: {
        marginTop: '10px',
    },
    country: {
        marginTop: '10px',
    },

    termsConditionLink: {
        color: '#1D8A8B',
        fontWeight: 700,
    },
    submitButton: { textTransform: 'capitalize', width: '100%', padding: 17, fontSize: 18, fontWeight: 500 },
    bottomNavContainer: {
        marginBottom: 26,
        [theme.breakpoints.down('md')]: { marginBottom: 27 },
        [theme.breakpoints.down('sm')]: { marginBottom: 32 },
    },
    bottomNavLinks: {
        fontWeight: 400,
        fontSize: '13px',
        lineHeight: '15px',
        textAlign: 'center',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    bottomNavALink: {
        color: 'rgba(17, 30, 20, 0.4)',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
    },
    createAccountLink: {
        color: '#1D8A8B',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
var isDevMode = false;
window.location.hostname === 'localhost' ? (isDevMode = true) : (isDevMode = false);

const getDefns = params => {
    const {
        classes,
        // props,
        scrollToForm,
        formik,
        // formSubmitted,
        // recaptcha,
        // onReCaptchaChange,
        handleClickCreateAccountLink,
        // country,
        // state,
        countryProps,
        stateLabelProps,
        stateProps,
        // city,
        // cityLocation,
        cityProps,
        phone,
        onChangePhone,
        phoneError,
        acceptedTerms,
        // setAcceptedTerms,
        handleChangeAcceptedTerms,
        termsConditionsRequiredMessageDialog,
        handleCloseTermsConditionsRequiredMessageDialog,
    } = params;

    const defns = {
        employerContainer: { container: true, justify: 'center' },
        divContainer: { container: true, justify: 'center' },
        internshipImgContainer: { item: true, xl: 11, lg: 11, md: 11, sm: 11, xs: 11 },

        mainContainer: { container: true, xl: 10, lg: 11, md: 10, sm: 11, xs: 11 },
        bannerContainer: { container: true, direction: 'row', justify: 'center' },
        bannerContent1: { item: true, xl: 11, lg: 11, md: 11, sm: 11, xs: 11 },
        bannerButton: {
            onClick: scrollToForm,
        },

        termsConditionLink: {
            color: 'secondary',
            href: '/home/FAQ/terms',
            target: '_blank',
        },
        createAccountLink: {
            onClick: handleClickCreateAccountLink,
        },

        contactsFormSection: { container: true, direction: 'row', justify: 'center' },
        contactsFormGrid: { item: true, xl: 7, lg: 11, md: 10, sm: 11, xs: 11 },
        formInputContainer: { container: true },
        formInputItem: { item: true, xl: 6, lg: 6, md: 6, sm: 12, xs: 12 },

        firstName: {
            ...formik.getFieldProps('firstName'),
            placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            id: 'firstName',
            error: formik.touched?.firstName && !!formik.errors?.firstName,
            helperText: formik.errors?.firstName,
            onChange: formik.handleChange,
        },
        lastName: {
            placeholder: PLACEHOLDER_FIELD_LAST_NAME,
            id: 'lastName',
            ...formik.getFieldProps('lastName'),
            error: formik.touched?.lastName && !!formik.errors?.lastName,
            helperText: formik.errors?.lastName,
        },
        email: {
            placeholder: PLACEHOLDER_FIELD_EMAIL,
            id: 'email',
            ...formik.getFieldProps('email'),
            error: formik.touched?.email && !!formik.errors?.email,
            helperText: formik.errors?.email,
        },
        jobTitle: {
            placeholder: PLACEHOLDER_FIELD_JOB_TITLE,
            id: 'jobTitle',
            ...formik.getFieldProps('jobTitle'),
        },
        phone: {
            placeholder: PLACEHOLDER_FIELD_PHONE,
            id: 'phone',
            // ...formik.getFieldProps('phone'),
            country: 'US',
            international: true,
            withCountryCallingCode: true,
            className: phoneError ? classes.phoneInputError : classes.phoneInput,
            value: phone,
            onChange: val => onChangePhone(val),
        },
        company: {
            placeholder: PLACEHOLDER_FIELD_COMPANY_NAME,
            id: 'company',
            ...formik.getFieldProps('company'),
            error: formik.touched?.company && !!formik.errors?.company,
            helperText: formik.errors?.company,
        },
        country: {
            ...countryProps,
            error: formik.touched?.country && Boolean(formik.errors?.country),
            helperText: formik.errors?.country,
        },
        stateLabel: {
            ...stateLabelProps,
        },
        state: {
            ...stateProps,
            error: formik.touched?.state && Boolean(formik.errors?.state),
            helperText: formik.errors?.state,
        },
        city: {
            ...cityProps,
            error: formik.touched?.city && Boolean(formik.errors?.city),
            helperText: formik.errors?.city,
        },
        acceptedTerms: {
            checked: acceptedTerms,
            onChange: handleChangeAcceptedTerms,
        },
        termsConditionsRequiredMessageDialog: {
            children: YOU_MUST_ACCEPT_TERMS_BEFORE_SUBMITTING,
            open: termsConditionsRequiredMessageDialog,
            onClose: handleCloseTermsConditionsRequiredMessageDialog,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const validate = (country, state, city, cityLocation, values) => {
    const errors = {};
    const fieldNames = ['email', 'firstName', 'lastName'];
    if (!isDevMode) fieldNames.push('reCaptcha');

    // required fields for login and sign up
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
        if (_get(values, 'email')) {
            const result = validateEmail(_get(values, 'email'));
            if (result === null) _set(errors, 'email', 'Please enter a valid email');
        }
    });

    if (!country) _set(errors, 'country', REQUIRED);
    if (country && !state) _set(errors, 'state', REQUIRED);
    if (state && !city) _set(errors, 'city', REQUIRED);

    return errors;
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Internship = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const theme = useTheme();
    const matchesXl = useMediaQuery(theme.breakpoints.up('xl'));
    const { userId, lovs, isEmp, authUser } = useContext(AppContext);

    const {
        // countryLov,
        // stateLov,
        // cityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        // setCityLocation,
        // newCity,
        // countryCurrency,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov'),
    });

    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop, setIsRequestOpenCreateAccountDialog, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);
    const [recaptcha, setReCaptcha] = React.useState(null);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [termsConditionsRequiredMessageDialog, setTermsConditionsRequiredMessageDialog] = useState(false);

    const registrationFormRef = useRef(null);

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: null,
            company: '',
            jobTitle: '',
            reCaptcha: '',
            country: 'United States',
            state: null,
            city: null,
            cityLocation: null,
        },
        validate: validate.bind(null, country, state, city, cityLocation),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => {
            const remainingFieldsValid = !phoneError;

            if (remainingFieldsValid) {
                let data = {
                    ..._pick(values, ['firstName', 'lastName', 'email', 'company', 'jobTitle']),
                    phone: formatPhoneNumberIntl(phone),
                    country,
                    state,
                    city,
                    cityLocation,
                };

                // if (userTypeError) validated = false;

                if (!acceptedTerms) {
                    setTermsConditionsRequiredMessageDialog(true);
                    return;
                }

                const success = await submitSummerIntershipEmployerRegistrationForm({ data, userId, setSnackbarMessage, setSnackbarErrorMessage, setFormSubmitted });
                if (success) {
                    formik.resetForm();
                    setCity(null);
                    setState(null);
                    setPhone(null);
                    setAcceptedTerms(false);
                    setReCaptcha(false);
                    setFormSubmitted(false);
                    scrollToPageTop();
                }
            }
        },
    });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_EMPLOYERS);
    }, []);

    useEffect(() => {
        setCountry('United States');
    }, [setCountry]);

    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    function scrollToForm() {
        registrationFormRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    const onReCaptchaChange = value => {
        setReCaptcha(value);
        formik.setFieldValue('reCaptcha', value, true);
    };

    const onChangePhone = val => {
        if (val) {
            setPhone(val);
            if (isPossiblePhoneNumber(val) && isValidPhoneNumber(val)) setPhoneError(false);
            else setPhoneError(true);
        } else {
            setPhoneError(false);
        }
    };

    const handleClickCreateAccountLink = () => {
        if (authUser) alert('Already logged in.');
        else setIsRequestOpenCreateAccountDialog && setIsRequestOpenCreateAccountDialog(true);
    };

    const handleChangeAcceptedTerms = evt => {
        setAcceptedTerms(evt.target.checked);
    };

    const handleCloseTermsConditionsRequiredMessageDialog = () => {
        setTermsConditionsRequiredMessageDialog(false);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        scrollToForm,
        formik,
        formSubmitted,
        recaptcha,
        onReCaptchaChange,
        handleClickCreateAccountLink,
        country,
        state,
        countryProps,
        stateLabelProps,
        stateProps,
        city,
        cityLocation,
        cityProps,
        phone,
        onChangePhone,
        phoneError,
        acceptedTerms,
        setAcceptedTerms,
        handleChangeAcceptedTerms,
        termsConditionsRequiredMessageDialog,
        handleCloseTermsConditionsRequiredMessageDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            {/* <AfitdPageHead
                title={PAGE_TITLE_INTERNSHIP_SEO}
                description="Our unique platform harnesses the power of storytelling through 60-second personality-driven videos to help businesses discover passionate and talented workers with potential."
                keywords="hire talented employees, 60-Second Interview Pitch, Interview Pitch"
                canonicalUrl={`https://${AFITD_DOMAIN}${PATH_HOME}/${PATH_INTERNSHIP}`}
            /> */}
            {/* banner section start */}
            <Grid {...defns.bannerContainer}>
                <Grid {...defns.bannerContent1}>
                    <p {...defns.bannerHeading}>internship program</p>

                    <p {...defns.bannerParagraph}>We are thrilled to announce our 2nd annual summer internship program!</p>

                    <div {...defns.buttonContainer}>
                        <Button variant="contained" color="secondary" value="Registration" {...defns.bannerButton}>
                            EMPLOYER Registration
                        </Button>
                    </div>
                </Grid>
                {matchesXl ? (
                    <div {...defns.bannerContent2}>
                        <img {...defns.circleImage} src={circle} alt="" />
                    </div>
                ) : null}
            </Grid>
            <Grid {...defns.divContainer}>
                <div {...defns.internshipProgramContainer}>
                    <p {...defns.internshipParagraph}>
                        As a job platform dedicated to helping individuals with 0-5 years of work experience, we understand the importance of gaining valuable skills and experience
                        to jump-start your career. That's why we have designed this program to provide opportunities for interns that will help you build your skill set and grow
                        your professional network.
                    </p>
                </div>
            </Grid>

            <Grid {...defns.divContainer}>
                <Grid {...defns.internshipImgContainer}>
                    <img {...defns.internshipImage} src={internshipImg} alt="" />
                </Grid>
            </Grid>

            <Grid {...defns.divContainer}>
                <div {...defns.internshipProgramTextContainer}>
                    <p {...defns.internshipProgram}>
                        Through this program, you'll have the chance to work with a diverse range of employers who are passionate about investing in the next generation of talent.
                        This will give you the chance to learn from experienced professionals in your field of interest, gain hands-on experience, and develop your skills in a
                        real-world setting. <br /> <br />
                        We believe that this program not only benefits our interns, but also the employers who participate. By offering paid/unpaid internships, employers have the
                        opportunity to mentor and shape the future leaders of their industry while also getting support from motivated and eager individuals. This mutually
                        beneficial relationship is a win-win for both parties and contributes to the growth of our community as a whole. Participating employers will receive a
                        30-day free trial of our job search platform, which will cover the duration of the program. <br /> <br />
                        <strong>Job Seekers:</strong> So, if you're ready to take the next step in your career, we encourage you to{' '}
                        <span {...defns.createAccountLink}>create your account here</span> to participate and apply to jobs in our summer internship program today!
                        <br /> <br />
                        <strong>Employers:</strong> Please complete the registration form below and our program coordinator will contact you.
                        <br /> <br />
                        We can't wait to see what we accomplish together through this program!
                        <br /> <br />
                    </p>
                    <p {...defns.internshipProgram} style={{ fontWeight: 'bold' }}>
                        Best regards, <br />A Foot in the Door Team
                    </p>
                </div>
            </Grid>

            {/* form section */}
            <Grid {...defns.contactsFormSection} ref={registrationFormRef}>
                <Grid {...defns.contactsFormGrid}>
                    <h1 {...defns.registrationFormTitle}>Employer Registration Form</h1>

                    <form onSubmit={formik.handleSubmit}>
                        <Grid {...defns.formInputContainer}>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                <label {...defns.fieldLevel} htmlFor="firstName">
                                    First Name: *
                                </label>
                                <AfitdTextField {...defns.firstName} />
                            </Grid>

                            <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                <label {...defns.fieldLevel} htmlFor="lastName">
                                    Last Name: *
                                </label>
                                <AfitdTextField id="lastName" {...defns.lastName} />
                            </Grid>
                        </Grid>
                        <Grid {...defns.formInputContainer}>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                <label {...defns.fieldLevel} htmlFor="email">
                                    Email: *
                                </label>
                                <AfitdTextField id="email" {...defns.email} />
                            </Grid>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                <label {...defns.fieldLevel} htmlFor="jobTitle">
                                    Job title:
                                </label>
                                <AfitdTextField id="jobTitle" {...defns.jobTitle} />
                            </Grid>
                        </Grid>
                        <Grid {...defns.formInputContainer}>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                {/* <label {...defns.fieldLevel} htmlFor="country">
                                    Select your Country: *
                                </label>
                                <AfitdCountryAutocomplete {...defns.country} /> */}
                                <label {...defns.fieldLevel} htmlFor="state">
                                    {/* {defns.stateLabel.value}: * */}
                                    Select your State: *
                                </label>
                                <AfitdCountryAutocomplete {...defns.state} />
                            </Grid>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                <label {...defns.fieldLevel} htmlFor="city">
                                    City: *
                                </label>
                                <AfitdCountryAutocomplete {...defns.city} />
                            </Grid>
                        </Grid>

                        <Grid {...defns.formInputContainer}>
                            <Grid {...defns.formInputItem} style={{ paddingRight: isEmp ? '10px' : '10px' }}>
                                <label {...defns.fieldLevel} htmlFor="phone">
                                    Phone:
                                </label>

                                <PhoneInput {...defns.phone} />
                                {phoneError && <AfitdTypography className={classes.phoneErrorText}>Phone number is not valid</AfitdTypography>}
                            </Grid>
                            <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                <label {...defns.fieldLevel} htmlFor="company">
                                    Company:
                                </label>
                                <AfitdTextField id="company" {...defns.company} />
                            </Grid>
                        </Grid>

                        <Grid {...defns.formInputContainer}>
                            <Grid {...defns.formInputItem} style={{ paddingRight: '10px', display: 'flex', alignItems: 'center' }}>
                                <AfitdCheckbox id="acceptedTerms" {...defns.acceptedTerms} />
                                <label htmlFor="acceptedTerms">
                                    Please accept <AfitdLink {...defns.termsConditionLink}>terms and conditions</AfitdLink>?
                                </label>
                            </Grid>
                            <Grid {...defns.formInputItem}>
                                {!isDevMode && (
                                    <div {...defns.reCaptchaContainer}>
                                        <Grid {...defns.reCaptchaContainer}>
                                            <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onReCaptchaChange} />
                                        </Grid>
                                        {!!formik.errors?.reCaptcha && recaptcha === '' && <AfitdTypography {...defns.reCaptchaError}>Please Verify yourself</AfitdTypography>}
                                    </div>
                                )}
                            </Grid>
                        </Grid>

                        <AfitdButton variant="contained" color="secondary" type="submit" value="Submit" {...defns.submitButton}>
                            Submit
                        </AfitdButton>
                    </form>
                </Grid>
            </Grid>
            {/*  <!-- bottom nav --> */}
            <Grid {...defns.bottomNavContainer}>
                <div>
                    <p {...defns.bottomNavLinks}>
                        <a {...defns.bottomNavALink} href="https://afitd-test.web.app/home/About-Us" target="_blank" rel="noopener noreferrer">
                            About Us
                        </a>{' '}
                        /{' '}
                        <a {...defns.bottomNavALink} href="https://afitd-test.web.app/home/FAQ/terms" target="_blank" rel="noopener noreferrer">
                            Terms
                        </a>{' '}
                        /{' '}
                        <a {...defns.bottomNavALink} href="https://afitd-test.web.app/home/FAQ/privacy" target="_blank" rel="noopener noreferrer">
                            Privacy
                        </a>{' '}
                        /{' '}
                        <a {...defns.bottomNavALink} href="https://afitd-test.web.app/home/Contact-Us" target="_blank" rel="noopener noreferrer">
                            Contact Us
                        </a>
                    </p>
                    <p {...defns.bottomNavLinks} style={{ marginTop: 12, opacity: 0.6 }}>
                        {getCopyrightTextInternship()}
                    </p>
                </div>
            </Grid>
            <AfitdMessageDialog {...defns.termsConditionsRequiredMessageDialog} />

            {/* ================== Login/Register Modal Section ================== */}
            {/* footer added */}
            <Footer />
        </div>
    );
};

export default Internship;
